// Step 3 — which value combinations get which attribute set (prototype
// `workCombos`): the coverage bar, then the same rules seen two ways — Rules
// (a searchable value picker per cell) and Grid (paint cells, rows and
// columns). A record gets every set that matches it.

import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Copy, Eye, Grid2x2, List, Plus, RefreshCw } from 'lucide-react'
import { tw } from '../../testHooks'
import { MAX_DRIVERS } from '../limits'
import { driverLabel, formOf, newSet, tenantBodyOf } from './model'
import { opForm, useSetup } from './state'
import { formTotals, type ValuesByDriver } from './coverage'
import RulesView from './RulesView'
import GridView from './GridView'
import { Banner, Seg } from './ui'

/** The deciding values of a form's drivers, as code lists. */
export function useValuesByDriver(formNo: string): { values: ValuesByDriver; ready: boolean } {
  const { draft, domains } = useSetup()
  const form = formOf(draft.layer, formNo)
  return useMemo(() => {
    const values: ValuesByDriver = {}
    let ready = true
    for (const d of form?.drivers || []) {
      const dom = domains.domainOf(formNo, d)
      if (dom.loading) ready = false
      values[d.key] = dom.values.map(v => v[0])
    }
    return { values, ready }
  }, [form?.drivers, domains, formNo])
}

/** Totals for the bar: the server's (one or two deciding columns), counted
 *  with the twin algorithm across all pages for three. */
function useTotals(formNo: string) {
  const { draft, transport, objName, mode } = useSetup()
  const form = formOf(draft.layer, formNo)
  const { values, ready } = useValuesByDriver(formNo)
  const local = useMemo(() => (form && ready ? formTotals(form, values) : null), [form, values, ready])
  const [server, setServer] = useState<{ all: number; covered: number; none: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const seq = useRef(0)
  const drivers = form?.drivers || []
  const useServer = mode === 'tenant' && drivers.length > 0 && drivers.length < MAX_DRIVERS
  const bodyKey = useServer ? JSON.stringify(tenantBodyOf(draft.layer, draft.design)) : ''
  useEffect(() => {
    if (!useServer) { setServer(null); return undefined }
    const my = ++seq.current
    const t = setTimeout(() => {
      transport.coverage(objName, {
        form_no: formNo, body: JSON.parse(bodyKey), rows_axis: drivers[0].key,
        cols_axis: drivers[1]?.key || null, row_limit: 1, col_limit: 1,
      }).then(r => { if (my === seq.current) { setServer(r.totals); setError(null) } })
        .catch(err => {
          console.warn('[uda setup] coverage failed', err)
          if (my === seq.current) setError((err as Error)?.message || 'Coverage could not be counted')
        })
    }, 250)
    return () => clearTimeout(t)
  }, [useServer, bodyKey, transport, objName, formNo]) // eslint-disable-line react-hooks/exhaustive-deps
  return { totals: useServer ? (server || local) : local, error, ready }
}

export default function StepCombinations() {
  const { formNo, draft, domains, cview, setCview, openDialog, notify, additionsAllowed } = useSetup()
  const form = formOf(draft.layer, formNo)
  const { totals, error } = useTotals(formNo)
  const [gridFocus, setGridFocus] = useState<{ q?: string; onlyGaps?: boolean; nonce: number } | null>(null)
  const drivers = form?.drivers || []
  const fresh = domains.newValues.filter(n => n.formNo === formNo)
  const last = domains.lastRefresh
  const addSet = () => {
    if (!additionsAllowed(formNo)) { notify('info', 'The application does not allow adding attribute sets here.'); return }
    if (!drivers.length) { notify('info', 'Choose the deciding columns first.'); return }
    const label = 'New attribute set'
    draft.edit(opForm(formNo, f => { f.sets.push(newSet(f, label)) }))
    setCview('rules')
  }
  if (!form || !drivers.length) {
    return (
      <>
        <div className="uds-lead"><h2>3. Decide which combinations get which attribute set</h2></div>
        <Banner tone="i">Choose at least one deciding column first (step 2).</Banner>
      </>
    )
  }
  const fmt = (n: number) => n.toLocaleString()
  return (
    <>
      <div className="uds-lead">
        <h2>3. Decide which combinations get which attribute set</h2>
        <p>Each attribute set says which values it applies to. Pick values from a searchable list in <b>Rules</b>,
          or paint them on the <b>Grid</b>. <b>Any</b> also covers values added to the master later. A record gets
          every set that matches it.</p>
      </div>
      {fresh.map(n => {
        const d = drivers.find(x => x.key === n.driverKey)
        return (
          <Banner key={`${n.driverKey}|${n.code}`} tone="w" action={(
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={() => { setCview('grid'); setGridFocus({ q: n.label, nonce: Date.now() }) }}>Show it on the grid</button>
          )}>
            <b>New value in {d ? driverLabel(d) : n.driverKey}:</b> {n.label}{n.count != null ? ` (${fmt(n.count)} records)` : ''}.
            {' '}Only sets with Any cover it.
          </Banner>
        )
      })}
      <div className="uds-covbar">
        <div className="uds-covnum"><b>{totals ? fmt(totals.all) : '…'}</b><span>combinations</span></div>
        <div className="uds-covnum"><b>{totals ? fmt(totals.covered) : '…'}</b><span>get attributes</span></div>
        <div className={`uds-covnum ${totals?.none ? 'warn' : ''}`}><b>{totals ? fmt(totals.none) : '…'}</b><span>get none</span></div>
        <span className="uds-grow" />
        {error && <span style={{ fontSize: 13, color: 'var(--color-error)' }}><AlertTriangle size={14} /> {error}</span>}
        {!!totals?.none && (
          <button type="button" className="btn btn-secondary btn-sm"
            onClick={() => { setCview('grid'); setGridFocus({ onlyGaps: true, nonce: Date.now() }) }}>
            <Eye size={14} /> Show the gaps
          </button>
        )}
      </div>
      <div className="uds-card">
        <div className="uds-card-h">
          <Seg value={cview} onChange={setCview} ariaLabel="View"
            options={[{ value: 'rules', label: <><List size={14} /> Rules</> }, { value: 'grid', label: <><Grid2x2 size={14} /> Grid</> }]} />
          <span className="uds-grow" />
          <button type="button" className="btn btn-secondary btn-sm" disabled={domains.refreshing}
            onClick={() => {
              void domains.refresh(formNo, drivers).then(found => notify('success', found.length
                ? `Values read again: ${found.length} new value${found.length === 1 ? '' : 's'}.`
                : 'Values read again from the masters.'))
            }} {...tw('action', 'uda-refresh-values')}>
            {domains.refreshing ? <span className="uds-spin" /> : <RefreshCw size={14} />}
            Refresh values{last ? ` · ${last.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => openDialog({ kind: 'import' })}
            {...tw('action', 'uda-import')}>
            <Copy size={14} /> Import / export
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={addSet} {...tw('action', 'uda-add-set')}>
            <Plus size={14} /> New attribute set
          </button>
        </div>
        <div className="uds-card-b" style={{ padding: cview === 'grid' ? 14 : 0 }}>
          {cview === 'grid' ? <GridView focus={gridFocus} /> : <RulesView />}
        </div>
      </div>
    </>
  )
}
